import { useEffect, useMemo, useState } from "react";
import {
  Palette,
  Check,
  Sparkles,
  Image as ImageIcon,
  Link2,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import HeroSection from "../../../components/ui/HeroSection";
import Button from "../../../components/ui/Button";
import ImageInput from "../../../components/ui/ImageInput";

type Mode = "default" | "company" | "custom";

const inputCls =
  "w-full px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400";

// Configure the white-label branding shown on the public link gate pages
// (password / expired / lead-capture / not-found).
const Branding = ({ embedded = false }: { embedded?: boolean }) => {
  const services = useAppServices();
  const { account, refreshAccount } = useMarketplace();

  const [mode, setMode] = useState<Mode>(account?.branding?.mode || "default");
  const [name, setName] = useState(account?.branding?.name || "");
  const [logoUrl, setLogoUrl] = useState(account?.branding?.logo_url || "");
  const [faviconUrl, setFaviconUrl] = useState(
    account?.branding?.favicon_url || "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!account) return;
    setMode(account.branding?.mode || "default");
    setName(account.branding?.name || "");
    setLogoUrl(account.branding?.logo_url || "");
    setFaviconUrl(account.branding?.favicon_url || "");
  }, [account]);

  // Account branding is available if the install captured a name/logo.
  const companyAvailable = !!(
    account?.name ||
    account?.company_name ||
    account?.logo_url
  );

  const preview = useMemo(() => {
    if (mode === "custom")
      return {
        name: name || account?.name || "Smart Links",
        logo: logoUrl || "",
      };
    if (mode === "company")
      return {
        name: account?.name || account?.company_name || "Smart Links",
        logo: account?.logo_url || "",
      };
    return { name: "Smart Links", logo: "" };
  }, [mode, name, logoUrl, account]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    const { error } = await services.account.updateBranding({
      payload: { mode, name, logo_url: logoUrl, favicon_url: faviconUrl },
    });
    setSaving(false);
    if (!error) {
      setSaved(true);
      await refreshAccount();
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const MODES: { key: Mode; title: string; desc: string; disabled?: boolean }[] =
    [
      {
        key: "default",
        title: "Default (Smart Links)",
        desc: "Show the standard Smart Links branding.",
      },
      {
        key: "company",
        title: account?.company_name
          ? `${account.company_name} branding`
          : "Account branding",
        desc: companyAvailable
          ? "Use your account's own name & logo."
          : "No account name/logo found for this account yet.",
        disabled: !companyAvailable,
      },
      {
        key: "custom",
        title: "Custom",
        desc: "Use your own brand name, logo and favicon.",
      },
    ];

  return (
    <div className="space-y-6">
      {!embedded && (
        <HeroSection
          eyebrow={
            <>
              <Palette size={11} className="text-brand-500" />
              <span>Branding</span>
            </>
          }
          title="White-label your link pages"
          description="Control the logo, name and favicon shown on your public link pages — the password, expired, lead-capture and not-found screens."
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">
        {/* Settings */}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5 space-y-5">
          <div className="space-y-2">
            {MODES.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => !opt.disabled && setMode(opt.key)}
                disabled={opt.disabled}
                className={`w-full text-left flex items-start gap-3 rounded-xl border p-3 transition-all ${
                  mode === opt.key
                    ? "border-brand-300 ring-2 ring-brand-100 bg-brand-50/40"
                    : "border-ink-200/70 hover:border-ink-300"
                } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                    mode === opt.key
                      ? "border-brand-500 bg-brand-500"
                      : "border-ink-300"
                  }`}
                >
                  {mode === opt.key && (
                    <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  )}
                </span>
                <span>
                  <span className="block text-sm font-medium text-ink-900">
                    {opt.title}
                  </span>
                  <span className="block text-[11px] text-ink-500 mt-0.5">
                    {opt.desc}
                  </span>
                </span>
              </button>
            ))}
          </div>

          {mode === "company" && (
            <div className="rounded-xl bg-ink-50/60 border border-ink-200/70 p-3 text-xs text-ink-600 space-y-1.5">
              <div>
                Name:{" "}
                <span className="font-medium text-ink-900">
                  {account?.name || account?.company_name || "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                Logo:{" "}
                {account?.logo_url ? (
                  <img
                    src={account.logo_url}
                    alt=""
                    className="h-6 rounded object-contain"
                  />
                ) : (
                  <span>—</span>
                )}
              </div>
              <p className="text-[11px] text-ink-400 pt-1">
                Pulled from your account's white-label settings at install.
              </p>
            </div>
          )}

          {mode === "custom" && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1.5">
                  Brand name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your brand"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1.5">
                  Logo
                </label>
                <ImageInput value={logoUrl} onChange={setLogoUrl} />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-700 mb-1.5">
                  Favicon{" "}
                  <span className="text-ink-400">(optional — defaults to logo)</span>
                </label>
                <ImageInput value={faviconUrl} onChange={setFaviconUrl} />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="gradient"
              onClick={save}
              loading={saving}
              icon={saved ? <Check size={14} /> : <Sparkles size={14} />}
            >
              {saved ? "Saved" : "Save branding"}
            </Button>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5">
          <div className="text-xs text-ink-500 mb-3">
            Preview — your link gate pages
          </div>
          <div className="rounded-2xl border border-ink-200 bg-ink-50/40 px-6 py-7 text-center">
            <div className="inline-flex items-center gap-2 mb-5">
              {preview.logo ? (
                <img
                  src={preview.logo}
                  alt=""
                  className="w-7 h-7 rounded-lg object-cover"
                />
              ) : (
                <span className="w-7 h-7 rounded-lg bg-brand-gradient flex items-center justify-center">
                  <Link2 size={14} className="text-white" strokeWidth={2.75} />
                </span>
              )}
              <span className="font-display font-bold text-ink-900 tracking-tight">
                {preview.name}
              </span>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center mx-auto mb-3">
              <ImageIcon size={20} />
            </div>
            <div className="font-display font-bold text-ink-900">
              This link is protected
            </div>
            <p className="text-xs text-ink-500 mt-1">
              Enter the password to continue.
            </p>
            <div className="mt-5 pt-3 border-t border-ink-200 text-[11px] text-ink-400">
              Secured by {preview.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Branding;
